import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  TlsProfilePrivileges,
} from 'data/tlsProfile/tlsProfile.types';
import * as actions from 'data/tlsProfile/tlsProfile.actions';
import * as certStatusActions from 'data/certStatus/certStatus.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/tlsProfile/tlsProfile.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import DistributorGrid from './components/TLSProfileGrid';
import { distributorColumnDefs } from '../../TLSProfile.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getTlsProfileList(state),
  selectedTlsProfile: selectors.getSelectedTlsProfile(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToTlsProfileCreate: actions.goToTlsProfileCreate,
  goToTlsProfileEdit: actions.goToTlsProfileEdit,
  goToTlsProfileView: actions.goToTlsProfileView,
  selectTlsProfile: actions.selectTlsProfile,
  getTlsProfileList: actions.getTlsProfileList,
  deleteTlsProfile: actions.deleteTlsProfile,
  getAllCertStatus: certStatusActions.getAllCertStatus
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const TlsProfileList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedTlsProfile,
  goToTlsProfileCreate,
  goToTlsProfileEdit,
  goToTlsProfileView,
  selectTlsProfile,
  getTlsProfileList,
  deleteTlsProfile,
  getAllCertStatus
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(distributorColumnDefs);

  useEffect(() => {
    getTlsProfileList(deliveryFunctionId);
    getAllCertStatus(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getTlsProfileList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getTlsProfileList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToTlsProfileCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectTlsProfile(event.api.getSelectedRows());
  };

  const handleDeleteTlsProfile = () => {
    const distributorConfigrationId = selectedTlsProfile[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedTlsProfile[0]?.name,
        AuditService.SECURITY_INFO,
        AuditActionType.SECURITY_INFO_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteTlsProfile(deliveryFunctionId, distributorConfigrationId, body);
  };

  const handleGoToEdit = () => goToTlsProfileEdit(history);

  const handleGoToView = () => goToTlsProfileView(history);

  const hasPrivilegeToView = privileges.includes(TlsProfilePrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(TlsProfilePrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(TlsProfilePrivileges.Delete);

  return (
    <Fragment>
      <DistributorGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedTlsProfile={selectedTlsProfile[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteTlsProfile}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(TlsProfileList));
